import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { MachineContext } from '../DataContexts/MachineContext';
import { API_BASE_URL } from '../config';
import { DashboardWidget, WidgetScopeControl } from './DashboardWidgets';
import './EventLogsWidget.css';

// DJB-152/DJB-196: Event Logs widget on the ActivityController's
// /logbooks/search endpoint: everything from the last 10 days (globals
// included — the endpoint defaults includeGlobalActivities to true), with a
// top-right Manual/Machine/Unknown/All source toggle mirroring the Accounts
// Info Event Logs block (DJB-187), grouped under per-day date headers.
const DAYS_SHOWN = 10;
const PAGE_SIZE = 100;
// The search endpoint is paginated; keep pulling pages until one comes back
// short so the whole window shows. The cap only guards against a runaway feed.
const MAX_PAGES = 10;

const SOURCE_FILTERS = [
  { key: 'MANUAL', label: 'Manual' },
  { key: 'MACHINE', label: 'Machine' },
  { key: 'UNKNOWN', label: 'Unknown' },
  { key: 'ALL', label: 'All' },
];

// The backend serializes LocalDateTime as [y, m, d, h, min, s(, nanos)] like
// the machine timestamps, but tolerate ISO strings should that config change
const toDate = (timestamp) => {
  if (Array.isArray(timestamp)) {
    if (timestamp.length < 3) return null;
    return new Date(
      timestamp[0], timestamp[1] - 1, timestamp[2],
      timestamp[3] || 0, timestamp[4] || 0, timestamp[5] || 0
    );
  }
  return timestamp ? new Date(timestamp) : null;
};

// Rows only carry the time of day — the calendar day lives in the group header
const formatEventTime = (timestamp) => {
  const date = toDate(timestamp);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
};

const dayKey = (date) =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const formatDayTitle = (date) => {
  if (!date || Number.isNaN(date.getTime())) return 'Unknown date';
  const formatted = date.toLocaleDateString('nl-BE', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const today = new Date();
  if (dayKey(date) === dayKey(today)) return `Today · ${formatted}`;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(date) === dayKey(yesterday)) return `Yesterday · ${formatted}`;
  return formatted;
};

// Start of the oldest calendar day in the window (today counts as day 1),
// as the ISO local date-time the fromDateTime param expects
const windowStart = () => {
  const from = new Date();
  from.setDate(from.getDate() - (DAYS_SHOWN - 1));
  const pad = (part) => String(part).padStart(2, '0');
  return `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}T00:00:00`;
};

const SOURCE_LABELS = { MANUAL: 'Manual', MACHINE: 'Machine', UNKNOWN: 'Unknown' };

export const EventLogsWidget = () => {
  const { authData } = useContext(AuthContext) || {};
  const accessToken = authData?.accessToken;
  const { machines = [] } = useContext(MachineContext);
  const [activities, setActivities] = useState(null);
  const [error, setError] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('ALL');

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }
    let cancelled = false;
    setActivities(null);
    setError(null);
    const loadWindow = async () => {
      const all = [];
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const params = new URLSearchParams({
          fromDateTime: windowStart(),
          page: String(page),
          size: String(PAGE_SIZE),
        });
        if (sourceFilter !== 'ALL') {
          params.append('logsources', sourceFilter);
        }
        const response = await fetch(`${API_BASE_URL}/logbooks/search?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch event logs (${response.status})`);
        }
        const body = await response.json();
        const batch = Array.isArray(body) ? body : [];
        all.push(...batch);
        if (batch.length < PAGE_SIZE) {
          break;
        }
      }
      return all;
    };
    loadWindow()
      .then((all) => {
        if (!cancelled) {
          setActivities(all);
        }
      })
      .catch((fetchError) => {
        console.error('Failed to fetch /logbooks/search:', fetchError);
        if (!cancelled) {
          setError(fetchError.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, sourceFilter]);

  // Resolve machineIds to names so machine-linked entries read like the rest
  // of the dashboard; unknown ids still link, showing the raw id
  const machineNames = useMemo(
    () => new Map(machines.map((machine) => [machine.id, machine.name])),
    [machines]
  );

  // Newest first by the timestamp each row shows (eventtime falls back to
  // logtime, same as the Accounts Overview block), then batched per calendar day
  const dayGroups = useMemo(() => {
    if (!activities) return null;
    const sorted = [...activities].sort((a, b) => {
      const aTime = toDate(a.eventtime || a.logtime)?.getTime() ?? 0;
      const bTime = toDate(b.eventtime || b.logtime)?.getTime() ?? 0;
      return bTime - aTime;
    });
    const groups = [];
    let current = null;
    sorted.forEach((activity) => {
      const date = toDate(activity.eventtime || activity.logtime);
      const valid = date && !Number.isNaN(date.getTime());
      const key = valid ? dayKey(date) : 'unknown';
      if (!current || current.key !== key) {
        current = { key, title: formatDayTitle(valid ? date : null), events: [] };
        groups.push(current);
      }
      current.events.push(activity);
    });
    return groups;
  }, [activities]);

  const renderRow = (activity) => {
    const level = (activity.loglevel || 'LOW').toUpperCase();
    return (
      <div key={activity.id} className="event-log-row">
        <span className={`event-log-level ${level.toLowerCase()}`}>{level}</span>
        <div className="event-log-main">
          <div className="event-log-headline">
            <span className="event-log-subject">{activity.subject || 'Activity'}</span>
            {activity.content && activity.content !== activity.subject && (
              <span className="event-log-content">{activity.content}</span>
            )}
          </div>
          <div className="event-log-meta">
            <span>{formatEventTime(activity.eventtime || activity.logtime)}</span>
            <span>· {SOURCE_LABELS[activity.source] || activity.source || 'Unknown'}</span>
            {activity.machineId && (
              <span>
                {'· '}
                <Link className="event-log-machine" to={`/machines?tab=list&id=${activity.machineId}`}>
                  {machineNames.get(activity.machineId) || activity.machineId}
                </Link>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (error) {
      return <div className="widget-empty-note">Event logs are unavailable right now.</div>;
    }
    if (dayGroups === null) {
      return <div className="widget-empty-note">Loading recent activities…</div>;
    }
    if (dayGroups.length === 0) {
      return <div className="widget-empty-note">{`No activities in the last ${DAYS_SHOWN} days.`}</div>;
    }
    return (
      <div className="widget-rows event-log-list">
        {dayGroups.map((group) => (
          <div key={group.key} className="event-log-day">
            <div className="event-log-date-header">{group.title}</div>
            <div className="event-log-day-rows">
              {group.events.map(renderRow)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <DashboardWidget
      title="Recent Activity"
      variant="long"
      className="event-logs-widget"
      actions={(
        <WidgetScopeControl
          label="Filter events by source"
          options={SOURCE_FILTERS}
          value={sourceFilter}
          onChange={setSourceFilter}
        />
      )}
    >
      {renderBody()}
    </DashboardWidget>
  );
};

export default EventLogsWidget;
